import { sendMailSponsor } from './Mail_service';
import { UserModel } from '../models/UserModel';

interface EmailRecipient {
  email: string;
  name?: string;
}

interface EmailOptions {
  recipients: EmailRecipient[];
  customMessage?: string;
  customSubject?: string;
}

export class EmailSponsorService {
  private static getBaseUrl(): string {
    try {
      if (typeof window !== 'undefined' && window.location && window.location.origin) {
        return window.location.origin;
      }
    } catch (e) {
      // ignore en environnement non-navigateur
    }

    const envs = [
      process.env.REACT_APP_FRONT_URL,
      process.env.FRONTEND_URL,
    //   process.env.FRONT_END_BASE_URL,
    //   process.env.REACT_APP_BASE_URL,
    //   process.env.FRONT_END_BASE_URL,
    ];

    for (const v of envs) {
      if (v && typeof v === 'string' && v.trim() !== '') {
        return v.replace(/^['"]+|['"]+$/g, '');
      }
    }

    return 'http://localhost:3000';
  }

  private static generateReferralLinkFor(user?: UserModel | null): string {
    const base = this.getBaseUrl();
    const code = user?.code_parrainage;

    // Normalise base sans slash final
    const normalizedBase = base.replace(/\/$/, '');

    return code
      ? `${normalizedBase}/register?ref=${encodeURIComponent(code)}`
      : `${normalizedBase}/register`;
  }

  private static formatUserName(user: UserModel): string {
    if (user.user_type === 'entreprise' || user.is_entreprise) {
      return user.raison_social || user.name || 'Utilisateur ALO';
    } else {
      const firstName = user.firstname || '';
      const lastName = user.name || '';
      return `${firstName} ${lastName}`.trim() || 'Utilisateur ALO';
    }
  }

  private static generateReferralSubject(): string {
    return 'Invitation à découvrir ALO-FR.fr – Annonce Légale Officielle';
  }

  private static generateReferralMessage(user: UserModel, referralLink: string): string {
    const userName = this.formatUserName(user);

    return `Bonjour,

Je t'invite à découvrir ALO-FR.fr (Annonce Légale Officielle) via mon lien de parrainage :

Inscription avec mon code : ${referralLink}

Avec ALO, tu profites de nombreux avantages :
- Des annonces légales parmi les moins chères du marché, grâce aux codes remise et aux offres proposées en continu sur le site
- Une attestation immédiate, disponible dès la validation de ton annonce
- Un accès direct aux données INPI, pour simplifier tes démarches
- Un système de points et parrainage, pour cumuler encore plus d'économies

N'hésite pas à t'inscrire via mon code et profiter dès maintenant de ces avantages.

Profite de ce code de réduction : <strong>BIENVENUE</strong>

Cordialement,
${userName}`;
  }

  // ---------- Méthodes publiques ----------
  public static generateDefaultReferralData(user: UserModel) {
    const referralLink = this.generateReferralLinkFor(user);

    return {
      subject: this.generateReferralSubject(),
      message: this.generateReferralMessage(user, referralLink),
    };
  }

  public static validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  }

  public static validateRecipients(recipients: EmailRecipient[]): {
    valid: EmailRecipient[];
    invalid: string[];
  } {
    const valid: EmailRecipient[] = [];
    const invalid: string[] = [];

    recipients.forEach((recipient) => {
      if (this.validateEmail(recipient.email)) {
        valid.push(recipient);
      } else {
        invalid.push(recipient.email);
      }
    });

    return { valid, invalid };
  }

  public static async sendReferralEmails(
    sender: UserModel,
    options: EmailOptions
  ): Promise<{ success: boolean; emailCount: number; failedCount: number; message: string }> {
    if (!sender) {
      throw new Error("L'expéditeur est requis");
    }

    if (!sender.email) {
      throw new Error("L'expéditeur doit avoir une adresse email valide");
    }

    if (!options || !options.recipients || options.recipients.length === 0) {
      throw new Error('Au moins un destinataire doit être spécifié');
    }

    // Construire le lien de parrainage (toujours une string absolue)
    const referralLink = this.generateReferralLinkFor(sender);

    // Message / sujet : priorité à l'utilisateur (options.customMessage), sinon générés
    const message = options.customMessage || this.generateReferralMessage(sender, referralLink);
    const subject = options.customSubject || this.generateReferralSubject();

    // Préparer les destinataires pour l'API (email + bcc)
    const recipients = options.recipients.map((r) => r.email);

    const payload = {
      name: this.formatUserName(sender),
      email: recipients[0], // premier destinataire
      object: subject,
      sender: sender.email,
      message,
      bcc: recipients.length > 1 ? recipients.slice(1) : [],
    };

    try {
      const response = await sendMailSponsor(payload);

      const emailCount = response?.emailCount ?? 0;
      const failedCount = response?.failedCount ?? 0;

      return {
        success: emailCount > 0,
        emailCount,
        failedCount,
        message: `${emailCount} email(s) envoyé(s) avec succès${failedCount > 0 ? `, ${failedCount} échec(s)` : ''}`,
      };
    } catch (err: any) {
      const msg = err?.message || "Erreur lors de l'envoi des emails de parrainage";
      console.error('EmailSponsorService.sendReferralEmails error:', err);
      throw new Error(msg);
    }
  }
}
